"use client";

import {JSX, useRef} from "react";

import Link from "next/link";

import SmartImage from "@/shared/ui/smart-image";

import {getSectionSpace} from "@/sections/use-section-space";

import {useModel} from "@/shared/hooks/state-management/use-model";
import {useTranslation} from "@/shared/i18n";
import {ShowMoreShowLessButton} from "@/shared/ui/buttons";
import {Typography} from "@/shared/ui/data-display/typography";
import {TextField} from "@/shared/ui/form/fields/text-field";
import {ContainerBase} from "@/shared/ui/layout/container-base";
import {scrollToPromise} from "@/shared/utils/scroll";

import type {NewsSectionData} from "./news-section";
import {NewsSectionModel} from "./news-section.model";

import "./styles/news-section.scss";

export function NewsSectionClient({title, items, initialVisibleCount = 24, space, anchorId}: NewsSectionData): JSX.Element | null {
    const {t} = useTranslation("common");
    const [model, snapshot] = useModel(NewsSectionModel, items, initialVisibleCount);
    const sectionRef = useRef<HTMLElement>(null);
    const sectionVars = getSectionSpace(space);
    const visibleItems = snapshot.getVisibleItems();
    const hasMore = snapshot.hasMore();
    const showReadLessButton = snapshot.showReadLessButton;
    const noResults = snapshot.noResults;

    const handleShowLess = async (): Promise<void> => {
        if (sectionRef.current) {
            const offset = 120;
            const y = sectionRef.current.getBoundingClientRect().top + window.scrollY - offset;
            await scrollToPromise(y, {behavior: "smooth"});
        }
        model.showLess();
    };

    if (items.length === 0) {
        return null;
    }

    return (
        <section ref={sectionRef} className="news-section" style={sectionVars} id={anchorId}>
            <ContainerBase>
                <div className="news-section__header">
                    {title && (
                        <Typography as="h2" size={{desktop: 32, mobile: 20}} className="news-section__title">
                            {title}
                        </Typography>
                    )}
                    <div className="news-section__search">
                        <TextField model={model.searchField} type="search" placeholder="Search..." height="small" size="md" />
                    </div>
                </div>

                {noResults ? (
                    <Typography as="p" size={{desktop: 18, mobile: 14}} weight={500} className="news-section__no-results">
                        {t("noResultsFound")}
                    </Typography>
                ) : (
                    <div className="news-section__cards">
                        {visibleItems.map((item) => {
                            const articleHref = `/${item.slug}`;
                            return (
                                <article key={item.id} className="news-section__card">
                                    <Link href={articleHref} className="news-section__card-image">
                                        <SmartImage
                                            src={item.image.src}
                                            alt={item.image.alt}
                                            width={item.image.width ?? 640}
                                            height={item.image.height ?? 360}
                                        />
                                    </Link>
                                    <div className="news-section__card-content">
                                        <Link href={articleHref} className="news-section__card-title-link">
                                            <Typography as="h3" size={{desktop: 14, mobile: 12}} className="news-section__card-title">
                                                {item.title}
                                            </Typography>
                                        </Link>
                                        {item.cta?.url && item.cta?.text && (
                                            <Link href={item.cta.url} className="news-section__card-cta" target={item.cta.target}>
                                                <Typography as="span" size={{desktop: 12, mobile: 12}} color="paragraph" options={["underline"]}>
                                                    {item.cta.text}
                                                </Typography>
                                            </Link>
                                        )}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}

                {(hasMore || showReadLessButton) && (
                    <div className="news-section__show-more">
                        <ShowMoreShowLessButton
                            showReadLessButton={showReadLessButton}
                            hasMore={hasMore}
                            onShowLess={() => void handleShowLess()}
                            onShowMore={() => model.showMore()}
                        />
                    </div>
                )}
            </ContainerBase>
        </section>
    );
}
